/**
 * Job Openings — list.  GET /jobs
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import { PageHeader, Button, FloatingInput, FloatingDropdown, Pagination, TableSkeleton } from '../../components/ui';
import { Table, TableColumn } from '../../components/ui/DataDisplay';
import { useJobs } from '../../features/recruitment/hooks/useJobs';
import { usePeopleOptions } from '../../features/recruitment/hooks/useOptions';
import { JobStatusBadge, EmptyState, ErrorState } from '../../features/recruitment';
import { JOB_STATUS_OPTIONS } from '../../features/recruitment/config/constants';
import { formatDate, formatExperienceRange } from '../../features/recruitment/utils/format';
import type { JobOpening, JobStatus, JobOpeningFilters } from '../../services/recruitmentApi';

const PAGE_SIZE = 25;

export const JobOpeningsList: React.FC = () => {
	const navigate = useNavigate();

	const [status, setStatus] = useState<JobStatus | ''>('');
	const [hiringManagerId, setHiringManagerId] = useState('');
	const [facility, setFacility] = useState('');
	const [query, setQuery] = useState('');
	const [applied, setApplied] = useState<JobOpeningFilters>({ page: 1, page_size: PAGE_SIZE });
	const [page, setPage] = useState(1);

	const { data: people } = usePeopleOptions();
	const peopleById = useMemo(() => new Map((people ?? []).map(p => [p.value, p.label])), [people]);

	const { data, isLoading, isError, refetch, isFetching } = useJobs({ ...applied, page });

	const rows = (data?.rows ?? []) as JobOpening[];
	const total = data?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	const activeFilterCount = [status, hiringManagerId, facility, query].filter(Boolean).length;

	const runSearch = () => {
		setPage(1);
		setApplied({
			page: 1,
			page_size: PAGE_SIZE,
			status: status || undefined,
			hiring_manager_id: hiringManagerId ? Number(hiringManagerId) : undefined,
			facility_key: facility.trim() || undefined,
			q: query.trim() || undefined,
		});
	};

	const clearFilters = () => {
		setStatus('');
		setHiringManagerId('');
		setFacility('');
		setQuery('');
		setPage(1);
		setApplied({ page: 1, page_size: PAGE_SIZE });
	};

	const columns: TableColumn<Record<string, unknown>>[] = useMemo(
		() => [
			{
				key: 'title',
				title: 'Title',
				align: 'left',
				render: (_v, record) => {
					const r = record as unknown as JobOpening;
					return <span className='font-semibold text-gray-900'>{r.title}</span>;
				},
			},
			{
				key: 'facility_key',
				title: 'Location',
				align: 'left',
				render: (_v, record) => {
					const r = record as unknown as JobOpening;
					return <span className='text-gray-700'>{r.facility_key || '—'}</span>;
				},
			},
			{
				key: 'openings_count',
				title: 'Openings',
				align: 'center',
				render: (_v, record) => {
					const r = record as unknown as JobOpening;
					return <span className='text-gray-700'>{r.openings_count}</span>;
				},
			},
			{
				key: 'experience',
				title: 'Experience',
				align: 'center',
				render: (_v, record) => {
					const r = record as unknown as JobOpening;
					return <span className='text-gray-700'>{formatExperienceRange(r.experience_min, r.experience_max)}</span>;
				},
			},
			{
				key: 'hiring_manager_id',
				title: 'Hiring Manager',
				align: 'left',
				render: (_v, record) => {
					const r = record as unknown as JobOpening;
					const name = r.hiring_manager_id ? peopleById.get(String(r.hiring_manager_id)) : null;
					return <span className='text-gray-700'>{name || (r.hiring_manager_id ? `#${r.hiring_manager_id}` : '—')}</span>;
				},
			},
			{
				key: 'status',
				title: 'Status',
				align: 'center',
				render: (_v, record) => {
					const r = record as unknown as JobOpening;
					return <JobStatusBadge status={r.status} />;
				},
			},
			{
				key: 'date_opened',
				title: 'Date Opened',
				align: 'center',
				render: (_v, record) => {
					const r = record as unknown as JobOpening;
					return <span className='text-gray-700'>{formatDate(r.date_opened)}</span>;
				},
			},
			{
				key: 'application_count',
				title: 'Applications',
				align: 'center',
				render: (_v, record) => {
					const r = record as unknown as JobOpening;
					return <span className='font-medium text-gray-900'>{r.application_count ?? 0}</span>;
				},
			},
		],
		[peopleById]
	);

	return (
		<div className='min-h-screen bg-gray-50 p-6'>
			<div className='mx-auto max-w-7xl'>
				<div className='mb-6 flex items-start justify-between'>
					<PageHeader title='Job Openings' totalItems={total} itemType='openings' icon='💼' />
					<Button variant='primary' leftIcon={<Plus className='h-4 w-4' />} onClick={() => navigate('/recruitment/jobs/new')}>
						New Opening
					</Button>
				</div>

				{/* Filters */}
				<div className='mb-6 rounded-lg bg-white p-4 shadow-sm'>
					<div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4'>
						<FloatingInput label='Search by title' value={query} onChange={setQuery} />
						<FloatingDropdown
							label='Status'
							options={JOB_STATUS_OPTIONS}
							value={status}
							onChange={v => setStatus(v as JobStatus)}
							searchable={false}
						/>
						<FloatingDropdown
							label='Hiring Manager'
							options={people ?? []}
							value={hiringManagerId}
							onChange={setHiringManagerId}
						/>
						<FloatingInput label='Location / Facility' value={facility} onChange={setFacility} />
					</div>
					<div className='mt-4 flex items-center justify-between'>
						<span className='text-sm text-gray-500'>
							{activeFilterCount > 0 ? `${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} applied` : 'No filters applied'}
						</span>
						<div className='flex gap-2'>
							{activeFilterCount > 0 && (
								<Button variant='ghost' size='sm' leftIcon={<X className='h-3.5 w-3.5' />} onClick={clearFilters}>
									Clear
								</Button>
							)}
							<Button variant='primary' size='sm' onClick={runSearch} loading={isFetching}>
								Apply
							</Button>
						</div>
					</div>
				</div>

				{/* Content */}
				{isLoading ? (
					<TableSkeleton rows={8} columns={8} />
				) : isError ? (
					<ErrorState onRetry={() => refetch()} />
				) : rows.length === 0 ? (
					<div className='rounded-lg bg-white shadow-sm'>
						<EmptyState
							title='No job openings found'
							message={activeFilterCount > 0 ? 'Try adjusting or clearing your filters.' : 'Create your first opening to start hiring.'}
							action={
								<Button variant='primary' leftIcon={<Plus className='h-4 w-4' />} onClick={() => navigate('/recruitment/jobs/new')}>
									New Opening
								</Button>
							}
						/>
					</div>
				) : (
					<>
						<div className='overflow-hidden rounded-lg bg-white shadow-sm'>
							<Table
								columns={columns}
								data={rows as unknown as Record<string, unknown>[]}
								size='sm'
								onRowClick={record => navigate(`/recruitment/jobs/${(record as unknown as JobOpening).id}`)}
							/>
						</div>
						<div className='mt-4'>
							<Pagination
								currentPage={page}
								totalPages={totalPages}
								totalItems={total}
								itemsPerPage={PAGE_SIZE}
								onPageChange={setPage}
							/>
						</div>
					</>
				)}
			</div>
		</div>
	);
};
